/**
 * Author-only file field for journey map `resource_file` items.
 *
 * Two ways to attach a file, which is the point of this control:
 *
 *  - **Drop a new one.** The bytes go straight to S3 and a file-backed `resource` row is
 *    created. The step stores `resource.id` in `content_ref_id`.
 *  - **Pick a registered one.** Search the existing file resources and reference one that is
 *    already uploaded, so the same PDF referenced from six journeys is stored once.
 *
 * The library search reads the database (`db/resource-file/`), not the bucket. Listing S3
 * directly would surface objects with no row behind them — no verified content type, size or
 * display name — which is the exact state the upload handshake exists to prevent.
 *
 * Unlike `JourneyMapFileDropper`, this needs no journey map id: a file resource belongs to the
 * toolbox rather than to one map, so it can be attached while the map is still an unsaved
 * draft. That is the practical reason to prefer it for new steps.
 *
 * Delete is deliberately absent. A file resource is shared, so removing the bytes could break
 * other steps that reference it; "Remove" here only clears this step's reference. Deleting the
 * resource itself is a library-level action.
 *
 * Storage may be unconfigured entirely (the API answers 503), in which case the author falls
 * back to typing a reference by hand, matching the behaviour of the older dropper.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  LinearProgress,
  Link,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import CollectionsBookmarkOutlinedIcon from '@mui/icons-material/CollectionsBookmarkOutlined';
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';
import {
  ResourceFileRecord,
  ResourceFileStorageUnavailableError,
  ResourceRenderKind,
  downloadResourceFile,
  formatFileSize,
  getResourceFile,
  listResourceFiles,
  replaceResourceFile,
  uploadResourceFile,
} from '../../../../support/resourceFiles';
import { detectMp4FastStart } from '../../../../support/videoFastStart';

type Props = {
  /** `content_ref_id` of the item: the id of the file-backed `resource` row in use. */
  value?: string | number | null;
  onChange: (record: ResourceFileRecord | null) => void;
  /** Free-text fallback used when storage is not configured. */
  onManualReferenceChange?: (value: string) => void;
  /**
   * Optional `accept` attribute for the file picker (e.g. `video/mp4,video/webm`). A hint to the
   * OS dialog only — the server re-validates the content type on upload — but it keeps the video
   * editor from offering arbitrary files.
   */
  accept?: string;
  /**
   * Restrict the File Library browse/search to a single render kind (server-side filter). The
   * video editor sets this to `video` so its library shows videos only.
   */
  libraryRenderKind?: ResourceRenderKind;
  /**
   * Hide a render kind from the File Library (server-side `exclude_render_kind`). The file editor
   * passes `video` here to keep videos out of the general file library; the video editor uses
   * `libraryRenderKind` to show videos only. Excluded server-side, so the shown count and
   * pagination stay honest.
   */
  libraryExcludeKind?: ResourceRenderKind;
};

const RENDER_KIND_LABELS: Record<string, string> = {
  pdf: 'PDF',
  markdown: 'Markdown',
  spreadsheet: 'Spreadsheet',
  presentation: 'Presentation',
  image: 'Image',
  text: 'Text',
  video: 'Video',
  download: 'File',
};

const ResourceFileDropper: React.FC<Props> = ({
  value = null,
  onChange,
  onManualReferenceChange,
  accept,
  libraryRenderKind,
  libraryExcludeKind,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const replaceInputRef = useRef<HTMLInputElement | null>(null);

  const [record, setRecord] = useState<ResourceFileRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<'upload' | 'replace' | 'download' | null>(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // Non-blocking advisory (e.g. a video that isn't faststart-optimized). Distinct from `error`:
  // the upload succeeded, this is just guidance.
  const [warning, setWarning] = useState<string | null>(null);
  const [storageUnavailable, setStorageUnavailable] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const [libraryOpen, setLibraryOpen] = useState(false);
  const [librarySearch, setLibrarySearch] = useState('');
  const [libraryRows, setLibraryRows] = useState<ResourceFileRecord[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryTotal, setLibraryTotal] = useState(0);

  const resourceId = value == null || value === '' ? null : String(value);

  // Fetched rather than trusted from provider_config, so the panel shows the current name and
  // version even when another author replaced the bytes elsewhere.
  useEffect(() => {
    let active = true;

    const run = async () => {
      if (!resourceId) {
        if (active) setRecord(null);
        return;
      }

      setLoading(true);
      try {
        const found = await getResourceFile(resourceId);
        if (!active) return;

        setRecord(found);
        setStorageUnavailable(false);
      } catch (err: any) {
        if (!active) return;

        if (err instanceof ResourceFileStorageUnavailableError) {
          setStorageUnavailable(true);
        }

        setRecord(null);
      } finally {
        if (active) setLoading(false);
      }
    };

    void run();
    return () => {
      active = false;
    };
  }, [resourceId]);

  // Debounced so typing in the search box does not fire a request per keystroke.
  useEffect(() => {
    if (!libraryOpen) return;

    let active = true;
    const timer = window.setTimeout(async () => {
      setLibraryLoading(true);
      try {
        const result = await listResourceFiles({
          search: librarySearch,
          pageSize: 50,
          renderKind: libraryRenderKind,
          excludeRenderKind: libraryExcludeKind,
        });
        if (!active) return;

        setLibraryRows(result.items);
        setLibraryTotal(result.total_count);
      } catch (err: any) {
        if (!active) return;

        setLibraryRows([]);
        if (err instanceof ResourceFileStorageUnavailableError) setStorageUnavailable(true);
      } finally {
        if (active) setLibraryLoading(false);
      }
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [libraryOpen, librarySearch, libraryRenderKind, libraryExcludeKind]);

  const handleFailure = useCallback((err: any, fallback: string) => {
    if (err instanceof ResourceFileStorageUnavailableError) {
      setStorageUnavailable(true);
      setError(null);
      return;
    }

    setError(String(err?.message || fallback));
  }, []);

  /**
   * Advise (never block) when a just-uploaded video is not faststart-optimized, since that is the
   * usual cause of a video that will not start playing until it has fully downloaded. The check
   * is local and best-effort; anything inconclusive stays silent.
   */
  const checkFastStart = useCallback(async (file: File) => {
    if (!file.type.startsWith('video/')) return;

    const result = await detectMp4FastStart(file);
    if (result === 'not-faststart') {
      setWarning(
        'This video isn’t web-optimized, so it may be slow to start playing for learners. ' +
          'Re-encode it with “faststart” (e.g. ffmpeg -movflags +faststart) and re-upload for ' +
          'instant playback.',
      );
    }
  }, []);

  const handleUpload = useCallback(
    async (file: File) => {
      if (!file) return;

      setError(null);
      setWarning(null);
      setProgress(0);
      setBusy('upload');
      try {
        const created = await uploadResourceFile(file, {
          onProgress: (p) => setProgress(p.percent),
        });
        setRecord(created);
        onChange(created);
        void checkFastStart(file);
      } catch (err: any) {
        handleFailure(err, `Unable to upload ${file.name}.`);
      } finally {
        setBusy(null);
      }
    },
    [onChange, handleFailure, checkFastStart],
  );

  /**
   * Replace the bytes behind the resource this step already references.
   *
   * Worth being explicit about: because a file resource is shared, this changes the file for
   * *every* step referencing it. That is usually the intent (publishing a new revision of the
   * same document), which is why the copy below says so rather than hiding it.
   */
  const handleReplace = useCallback(
    async (file: File) => {
      if (!record?.id || !file) return;

      setError(null);
      setWarning(null);
      setProgress(0);
      setBusy('replace');
      try {
        const updated = await replaceResourceFile(record.id, file, (p) => setProgress(p.percent));
        setRecord(updated);
        onChange(updated);
        void checkFastStart(file);
      } catch (err: any) {
        handleFailure(err, `Unable to replace ${record.file_name || record.name}.`);
      } finally {
        setBusy(null);
      }
    },
    [record, onChange, handleFailure, checkFastStart],
  );

  const handleDownload = useCallback(async () => {
    if (!record?.id) return;

    setError(null);
    setBusy('download');
    try {
      await downloadResourceFile(record.id);
    } catch (err: any) {
      handleFailure(err, 'Unable to open the file.');
    } finally {
      setBusy(null);
    }
  }, [record, handleFailure]);

  /** Clears this step's reference only; the shared resource is left alone. */
  const handleUnlink = useCallback(() => {
    setRecord(null);
    setError(null);
    setWarning(null);
    onChange(null);
  }, [onChange]);

  const handlePickFromLibrary = useCallback(
    (picked: ResourceFileRecord) => {
      setRecord(picked);
      setLibraryOpen(false);
      setError(null);
      setWarning(null);
      onChange(picked);
    },
    [onChange],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setDragActive(false);

      const dropped = event.dataTransfer?.files?.[0];
      if (!dropped) return;

      // Dropping onto a step that already has a file registers a *new* resource rather than
      // replacing the shared one, which would change the file for every other step using it.
      // Replace stays an explicit button.
      void handleUpload(dropped);
    },
    [handleUpload],
  );

  const detailLine = useMemo(() => {
    if (!record) return '';

    const parts = [
      RENDER_KIND_LABELS[String(record.render_kind || '')] || record.content_type || '',
      formatFileSize(record.size_bytes),
    ].filter(Boolean);

    if (Number(record.version || 1) > 1) parts.push(`v${record.version}`);
    if (record.updated_by) parts.push(String(record.updated_by));

    return parts.join(' · ');
  }, [record]);

  const libraryDialog = (
    <Dialog open={libraryOpen} onClose={() => setLibraryOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle>File Library</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <TextField
            autoFocus
            size="small"
            fullWidth
            placeholder="Search by name, file name or description"
            value={librarySearch}
            onChange={(event) => setLibrarySearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {libraryLoading ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Searching…
              </Typography>
            </Stack>
          ) : libraryRows.length === 0 ? (
            <Alert severity="info">
              {librarySearch.trim()
                ? 'No registered files match that search.'
                : 'No files have been registered yet. Drop one in to add the first.'}
            </Alert>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary">
                {libraryTotal} registered file{libraryTotal === 1 ? '' : 's'}
                {libraryRows.length < libraryTotal ? ` · showing ${libraryRows.length}` : ''}
              </Typography>

              <List dense disablePadding>
                {libraryRows.map((row) => (
                  <ListItemButton
                    key={row.id}
                    onClick={() => handlePickFromLibrary(row)}
                    selected={String(row.id) === resourceId}
                  >
                    <InsertDriveFileOutlinedIcon
                      fontSize="small"
                      color="action"
                      sx={{ mr: 1.25 }}
                    />
                    <ListItemText
                      primary={row.name || row.file_name || `Resource ${row.id}`}
                      secondary={
                        [
                          row.file_name && row.file_name !== row.name ? row.file_name : '',
                          RENDER_KIND_LABELS[String(row.render_kind || '')] || '',
                          formatFileSize(row.size_bytes),
                        ]
                          .filter(Boolean)
                          .join(' · ') || undefined
                      }
                      primaryTypographyProps={{ sx: { wordBreak: 'break-all' } }}
                    />
                    {row.render_kind ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={RENDER_KIND_LABELS[String(row.render_kind)] || row.render_kind}
                      />
                    ) : null}
                  </ListItemButton>
                ))}
              </List>
            </>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );

  if (storageUnavailable) {
    return (
      <Stack spacing={1}>
        <Alert severity="warning">
          File storage is not configured for this environment, so uploads are unavailable. You can
          still record a reference by hand.
        </Alert>
        <TextField
          label="File Reference"
          size="small"
          value={resourceId || ''}
          onChange={(event) => onManualReferenceChange?.(event.target.value)}
          fullWidth
          helperText="Store an S3 key, resource id, or other backend file reference."
        />
      </Stack>
    );
  }

  const isBusy = busy !== null;

  return (
    <Stack spacing={1}>
      <Typography variant="caption" color="text.secondary">
        File
      </Typography>

      {loading ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Loading file…
          </Typography>
        </Stack>
      ) : record ? (
        <Box
          sx={{
            p: 1.25,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            backgroundColor: 'background.default',
          }}
        >
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <InsertDriveFileOutlinedIcon fontSize="small" color="action" />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
                  {record.file_name || record.name}
                </Typography>
                {detailLine ? (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {detailLine}
                  </Typography>
                ) : null}
                {record.is_active === false ? (
                  <Typography variant="caption" color="error" display="block">
                    This file has been removed from storage.
                  </Typography>
                ) : null}
              </Box>
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadOutlinedIcon />}
                onClick={handleDownload}
                disabled={isBusy || record.is_active === false}
              >
                {busy === 'download' ? 'Opening…' : 'Download'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<SwapHorizOutlinedIcon />}
                onClick={() => replaceInputRef.current?.click()}
                disabled={isBusy}
              >
                {busy === 'replace' ? `Replacing… ${progress}%` : 'Replace'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<CollectionsBookmarkOutlinedIcon />}
                onClick={() => setLibraryOpen(true)}
                disabled={isBusy}
              >
                Choose Another
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<LinkOffOutlinedIcon />}
                onClick={handleUnlink}
                disabled={isBusy}
              >
                Remove
              </Button>
            </Stack>

            {busy === 'replace' ? (
              <LinearProgress variant="determinate" value={progress} />
            ) : null}

            <Typography variant="caption" color="text.secondary">
              Replacing uploads new bytes to this same file resource, so every step referencing
              it serves the new version. “Remove” only clears this step’s reference and leaves
              the file in the library.
            </Typography>
          </Stack>

          <input
            ref={replaceInputRef}
            type="file"
            hidden
            accept={accept}
            onChange={(event) => {
              const selected = event.target.files?.[0];
              event.target.value = '';
              if (selected) void handleReplace(selected);
            }}
          />
        </Box>
      ) : (
        <Stack spacing={1}>
          <Box
            role="button"
            tabIndex={0}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                inputRef.current?.click();
              }
            }}
            onDragOver={(event) => {
              event.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
              textAlign: 'center',
              cursor: isBusy ? 'default' : 'pointer',
              border: '1px dashed',
              borderColor: dragActive ? 'primary.main' : 'divider',
              borderRadius: 1.5,
              backgroundColor: dragActive ? 'action.hover' : 'background.default',
              '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
            }}
          >
            {busy === 'upload' ? (
              <Box sx={{ width: '100%', px: 1 }}>
                <LinearProgress variant="determinate" value={progress} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.75 }}
                >
                  Uploading… {progress}%
                </Typography>
              </Box>
            ) : (
              <>
                <CloudUploadOutlinedIcon color="action" />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Drop a file here
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  or <Link component="span">browse</Link> to choose one
                </Typography>
              </>
            )}

            <input
              ref={inputRef}
              type="file"
              hidden
              accept={accept}
              onChange={(event) => {
                const selected = event.target.files?.[0];
                event.target.value = '';
                if (selected) void handleUpload(selected);
              }}
            />
          </Box>

          <Divider>
            <Typography variant="caption" color="text.secondary">
              or
            </Typography>
          </Divider>

          <Button
            size="small"
            variant="outlined"
            startIcon={<CollectionsBookmarkOutlinedIcon />}
            onClick={() => setLibraryOpen(true)}
            disabled={isBusy}
            fullWidth
          >
            Choose from File Library
          </Button>
        </Stack>
      )}

      {error ? <Alert severity="error">{error}</Alert> : null}

      {!error && warning ? (
        <Alert severity="warning" onClose={() => setWarning(null)}>
          {warning}
        </Alert>
      ) : null}

      {libraryDialog}
    </Stack>
  );
};

export default ResourceFileDropper;
